import { createFileRoute } from "@tanstack/react-router";
import {
  CheckCircle,
  FileText,
  Hammer,
  Truck,
} from "@phosphor-icons/react";
import { Fragment } from "react";
import { useTranslations } from "next-intl";
import {
  ForumPageChrome,
  ForumPageShell,
} from "@/components/forum-page-chrome";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/fleet")({
  component: FleetPage,
});

const ORDERS = [
  {
    brn: "ORD-2026-0087",
    model: "NW-42 Flagship",
    date: "2026-05-14",
    step: 3,
  },
  {
    brn: "ORD-2026-0091",
    model: "NW-35 Cruiser",
    date: "2026-06-02",
    step: 1,
  },
  {
    brn: "ORD-2025-0204",
    model: "NW-28 Power",
    date: "2025-12-10",
    step: 4,
  },
];

const STEPS = [
  { icon: FileText, labelKey: "fleetStepDraft" as const },
  { icon: CheckCircle, labelKey: "fleetStepQuoted" as const },
  { icon: Hammer, labelKey: "fleetStepProduction" as const },
  { icon: Truck, labelKey: "fleetStepDelivered" as const },
];

/** Landing OrderStatus + fleet shell — mock until C5. */
function FleetPage() {
  const t = useTranslations("app");

  return (
    <ForumPageShell>
      <ForumPageChrome
        eyebrow={t("fleetEyebrow")}
        title={
          <span className="inline-flex flex-wrap items-baseline gap-2">
            {t("navFleet")}
            <span className="text-ui-nano font-semibold uppercase tracking-wide text-[var(--brand-text-muted)] opacity-60">
              {t("navLater")}
            </span>
          </span>
        }
        intro={t("fleetIntro")}
      />

      <section className="mb-6 overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
        <div className="border-b border-[var(--brand-border)] bg-[var(--brand-navy)] px-4 py-2">
          <h2 className="text-ui-nano font-semibold uppercase tracking-[0.12em] text-[var(--brand-bg-page)]">
            {t("fleetRequestsTitle")}
          </h2>
        </div>
        <p className="border-b border-[var(--brand-border)] px-4 py-3 text-ui-micro text-[var(--brand-text-muted)] sm:px-6">
          {t("fleetRequestsIntro")}
        </p>
        <div className="divide-y divide-[var(--brand-border)]">
          {ORDERS.map((o) => (
            <div key={o.brn} className="p-4 sm:p-6">
              <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                <div>
                  <span className="font-mono text-ui-dense font-semibold text-[var(--brand-navy)]">
                    {o.brn}
                  </span>
                  <span className="ml-2 text-ui-micro text-[var(--brand-text-muted)]">
                    {o.model}
                  </span>
                </div>
                <span className="text-ui-micro text-[var(--brand-text-muted)]">
                  {o.date}
                </span>
              </div>
              <div className="flex items-center gap-1 sm:gap-0">
                {STEPS.map((s, si) => {
                  const Icon = s.icon;
                  const done = si < o.step;
                  const current = si === o.step;
                  return (
                    <Fragment key={s.labelKey}>
                      <div
                        className={cn(
                          "flex items-center gap-1.5 rounded-lg px-2 py-1 text-ui-micro",
                          done
                            ? "font-medium text-[var(--brand-navy)]"
                            : current
                              ? "bg-[rgb(var(--brand-navy-rgb)/0.05)] font-semibold text-[var(--brand-navy)]"
                              : "text-[var(--brand-text-muted)] opacity-40",
                        )}
                      >
                        <Icon
                          className={cn(
                            "size-3.5 shrink-0",
                            done
                              ? "text-emerald-600"
                              : current
                                ? "text-[var(--brand-navy)]"
                                : "text-[var(--brand-text-muted)] opacity-30",
                          )}
                          weight={done || current ? "fill" : "regular"}
                        />
                        <span className="hidden sm:inline">{t(s.labelKey)}</span>
                      </div>
                      {si < STEPS.length - 1 ? (
                        <div
                          className={cn(
                            "mx-1 h-px flex-1",
                            si < o.step
                              ? "bg-emerald-400"
                              : "bg-[var(--brand-border)]",
                          )}
                        />
                      ) : null}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-4 py-6 shadow-subtle sm:px-6">
        <h2 className="mb-1 font-heading text-ui-dense font-semibold text-[var(--brand-navy)]">
          {t("fleetHullsTitle")}
        </h2>
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("placeholderFleet")}
        </p>
      </section>
    </ForumPageShell>
  );
}
